import React from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../components/navigation/AuthenticationStateManager";
import NavigationTabBar from "../../components/navigation/NavigationTabBar";
import AuthenticationForm from "./components/AuthenticationForm";
import SecurityBadges from "./components/SecurityBadges";
import BenefitsSection from "./components/BenefitsSection";
import Icon from "../../components/AppIcon";

const AuthenticationPortal = () => {
  const { isAuthenticated, user, authLoading } = useAuth();
  const navigate = useNavigate();

  /* ---------- LOADING ---------- */
  if (authLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <p className="text-lg font-medium">Loading...</p>
      </div>
    );
  }

  /* ---------- ROLE BASED REDIRECT ---------- */
  if (isAuthenticated && user) {
    const role = user.role?.toUpperCase();

    if (role === "ADMIN") {
      return <Navigate to="/admin" replace />;
    }

    if (role === "STUDENT" || role === "USER") {
      return <Navigate to="/candidate/dashboard" replace />;
    }

    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      
      {/* Navigation */}
      <NavigationTabBar isAuthenticated={isAuthenticated} />

      <main className="pt-20 pb-12 md:pt-24 md:pb-16 lg:pt-28 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="text-center mb-8 md:mb-12 lg:mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 bg-primary/10 rounded-full mb-4 md:mb-6">
              <Icon name="User" size={36} color="var(--color-primary)" />
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-3 md:mb-4">
              Welcome to BoterVue
            </h1>

            <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
              Sign in to access personalized interview preparation tools,
              track your progress, and unlock premium features.
            </p>
          </div>

          {/* Login Form */}
          <AuthenticationForm />

          {/* Security Section */}
          <SecurityBadges />

          {/* Benefits */}
          <BenefitsSection />

          {/* Help Section */}
          <div className="mt-12 md:mt-16 lg:mt-20 text-center">
            <div className="bg-muted/30 rounded-lg p-6 md:p-8 lg:p-10">

              <div className="flex items-center justify-center mb-4 md:mb-6">
                <Icon
                  name="HelpCircle"
                  size={32}
                  color="var(--color-primary)"
                />
              </div>

              <h3 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground mb-3 md:mb-4">
                Need Help Getting Started?
              </h3>

              <p className="text-sm md:text-base text-muted-foreground mb-4 md:mb-6 max-w-2xl mx-auto">
                Our support team is available 24/7 to assist you with any
                questions about your account or platform features.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">

                <button
                  onClick={() => navigate("/about-platform")}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-base focus-ring text-sm md:text-base font-medium"
                >
                  Learn More About Us
                </button>

                <button
                  onClick={() => navigate("/pricing-plans")}
                  className="px-6 py-3 bg-card text-foreground border border-border rounded-md hover:bg-muted transition-base focus-ring text-sm md:text-base font-medium"
                >
                  View Pricing Plans
                </button>

              </div>
            </div>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-6 md:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">

            <div className="flex items-center space-x-2">
              <Icon
                name="GraduationCap"
                size={24}
                color="var(--color-primary)"
              />

              <span className="text-sm md:text-base text-muted-foreground">
                © {new Date().getFullYear()} BoterVue. All rights reserved.
              </span>
            </div>

            <div className="flex items-center space-x-4 md:space-x-6">

              <button className="text-xs md:text-sm text-muted-foreground hover:text-foreground transition-base">
                Privacy Policy
              </button>

              <button className="text-xs md:text-sm text-muted-foreground hover:text-foreground transition-base">
                Terms of Service
              </button>

              <button className="text-xs md:text-sm text-muted-foreground hover:text-foreground transition-base">
                Contact Support
              </button>

            </div>

          </div>

        </div>
      </footer>

    </div>
  );
};

export default AuthenticationPortal;